import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { AnimatePresence, motion, type Transition, type Variants } from 'framer-motion'
import { ListChecks, MessageSquarePlus, Sparkles, ThumbsUp, Undo2 } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { Button, Tooltip } from '@highlight/ui'
import { CopyIconButton } from '@renderer/overlay/components/copy-icon-button'
import { InvokeIcon } from '@renderer/components/invoke-icon'
import { feedbackSubject } from '@renderer/chat/api/feedback'
import { ActionWarning } from '@renderer/components/action-warning'
import { getActionIcon } from '@renderer/overlay/Sidepanel/utils/activity-helpers'
import { openInAppLink } from '@renderer/overlay/Mini/components/mini/nested/action-links'
import {
  isUndrafted as isUndraftedAction,
  sendBody,
} from '@renderer/overlay/Sidepanel/views/meeting-view/action-items/send-readiness'
import { isNativeTextEntryTarget } from '@renderer/utils/is-interactive-focused'
import { isReportToUserStagedAction } from '@renderer/chat/integrations/report-to-user'
import { isConversationStagedAction } from '@renderer/chat/integrations/conversation-action'
import {
  isEditableActionStatus,
  isStagedActionUserEdited,
  useStagedActionEditOptions,
  useStagedActionInvoke,
  useStagedActionStatus,
  useStagedActionUndo,
  type StagedAction,
} from '../../../hooks/use-staged-actions'
import { useActionInlineDraftController } from '../use-action-inline-draft-controller'
import { useDraftAction } from '../use-draft-action'
import { BraintrustSpanLink } from '../../../components/braintrust-span-link'
import { resolveActionSpanLink } from '../../../components/braintrust-links'
import { useConnectionError, ReconnectButton } from '../../../components/action-connection-error'
import {
  FLOATING_DANGER_FILL_CLASS,
  FLOATING_DANGER_SURFACE_CLASS,
  FLOATING_FILL_CLASS,
} from '@renderer/components/floating-surface'
import { FeedbackMenuButton } from '../../../components/feedback-menu-button'
import { trackActionEvent } from '../track-action-event'
import { trackStagedActionCopy, trackStagedActionOpenLink } from '../../../hooks/staged-actions/track-staged-action'
import { useActionArchiveControl, useAddActionAsTodo } from './action-utilities'
import { ActionViewOverflowMenu } from './overflow-menu'
import { ActionArchiveCircle } from '../../../components/action-archive-circle'
import { ActionViewChatCircle, ActionViewChatCircleNeighbours, type ActionViewChatControl } from './chat-control'
import type { ActionViewState } from '../action-view-state'
import type { ActionEditState } from '../../../atoms'

/**
 * The occupant of one morphing position in the control row.
 *
 * `key`, `node` and `onHotkey` are deliberately one value: which control is
 * showing, what it renders, and what ⌘↵ does are the same decision, and
 * splitting them into parallel conditionals is how they drift apart.
 * `onHotkey` absent means the key does nothing — which is also how a disabled
 * control stays disabled from the keyboard.
 */
interface ControlSlot {
  key: string
  node: ReactNode
  onHotkey?: () => void
}

// Only controls whose slot key changes animate; their neighbours retain position.
const slotVariants: Variants = {
  initial: { opacity: 0, y: 8, filter: 'blur(3px)' },
  animate: { opacity: 1, y: 0, filter: 'blur(0px)' },
  exit: { opacity: 0, y: 8, filter: 'blur(3px)', transition: { duration: 0.12, ease: 'easeOut' } },
}

const slotTransition: Transition = { duration: 0.16, ease: 'easeOut' }

/**
 * The CTA's label comes from the server ("Add Section to Documentation"), and
 * beside a live meeting's pill the row has no width to spare. It truncates
 * rather than running past the panel's right edge.
 */
const floatingCtaClass = 'min-w-0 shrink [&>svg]:shrink-0'

/**
 * What differs per row for the footer's outline controls, picked once so circles
 * and pills can't drift apart. Sent output cards set smaller type and glyphs.
 */
const FOOTER_SIZES = {
  default: { circle: FLOATING_FILL_CLASS, pill: FLOATING_FILL_CLASS, icon: 14 },
  minimal: {
    circle: twMerge(FLOATING_FILL_CLASS, '[&_svg:not([class*="size-"])]:size-3'),
    pill: twMerge(FLOATING_FILL_CLASS, 'text-xs [&_svg:not([class*="size-"])]:size-3'),
    icon: 12,
  },
}

function ActionViewMorphSlot({ slot, className }: { slot: ControlSlot | null; className?: string }) {
  return (
    <AnimatePresence mode="popLayout" initial={false}>
      {slot && (
        <motion.div
          key={slot.key}
          variants={slotVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={slotTransition}
          className={twMerge('flex shrink-0 items-center', className)}
        >
          {slot.node}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

/**
 * The footer's left-hand cluster opens chat, rates the draft, and links to its
 * trace in dev. It stays mounted across the Ready to Sent transition.
 */
function ActionViewUtilityCluster({
  action,
  braintrustLink,
  chat,
  onArchive,
}: {
  action: StagedAction
  braintrustLink?: string
  chat?: ActionViewChatControl
  onArchive?: () => void
}) {
  const spanLink = resolveActionSpanLink(action, braintrustLink)

  const feedback = feedbackSubject(action)

  return (
    // relative: the displaced controls exit with `popLayout`, which positions
    // them absolutely against the nearest positioned box.
    <div className="relative flex shrink-0 items-center gap-2">
      {chat && <ActionViewChatCircle {...chat} />}
      {/* The turn is rewriting this draft, so rating and archiving it are
          premature until it settles. */}
      <ActionViewChatCircleNeighbours hidden={chat?.applyingChanges === true}>
        {feedback && (
          <FeedbackMenuButton
            subject={feedback.subject}
            currentRating={feedback.initialRating}
            source="action"
            braintrustLink={spanLink}
            onArchive={onArchive}
          />
        )}
        {/* Dev-only affordance — the feedback menu carries it as a copy item; the
            standalone circle only steps in when there's no menu to hold it. */}
        {!feedback && spanLink && (
          <BraintrustSpanLink link={spanLink} variant="outline" className={FLOATING_FILL_CLASS} />
        )}
      </ActionViewChatCircleNeighbours>
    </div>
  )
}

/**
 * The row's error copy. This bar floats over the scrolling thread, where the
 * `bg-danger/10` wash a card would use lets the chat read through the message
 * (HIG-6220). `self-start` sizes it to the text instead of the whole bar.
 */
function ActionViewErrorBanner({ children }: { children: ReactNode }) {
  return (
    <div
      className={twMerge(
        'border-danger text-danger-subtle type-ui-xs corner-smooth self-start rounded-lg border px-3 py-2 shadow-lg',
        FLOATING_DANGER_SURFACE_CLASS,
      )}
    >
      {children}
    </div>
  )
}

interface ActionViewControlRowProps {
  action: StagedAction
  state: ActionViewState
  editState: ActionEditState | undefined
  braintrustLink?: string
  /** Renders the floating chat circle at the left of the cluster when set. */
  chat?: ActionViewChatControl
  enableHotkeys?: boolean
  /**
   * Keeps the hotkey chips rendered while the key binding itself is off —
   * the chat output holds them through the row's collapse animation so they
   * don't pop out of the buttons mid-fade. Defaults to `enableHotkeys`.
   */
  showHotkeys?: boolean
  /**
   * A live meeting holds the left of the floating bar, so everything but the
   * CTA folds into the overflow menu (Figma 8004:32399).
   */
  isCompact?: boolean
  /** Sent output cards keep only Undo and destination buttons. */
  minimalSentControls?: boolean
}

/**
 * The footer's control row is shared by Ready and Sent. Chat and feedback stay
 * on the left. Archive, utility, and the CTA stay on the right. Executing only
 * morphs copy into undo and execute into the output link.
 */
export function ActionViewControlRow({
  action,
  state,
  editState,
  braintrustLink,
  chat,
  isCompact = false,
  enableHotkeys = true,
  showHotkeys = enableHotkeys,
  minimalSentControls = false,
}: ActionViewControlRowProps) {
  const { status, errorMessage, isInvoking: isInvokingThisWindow } = useStagedActionStatus(action)
  const { invokeStagedAction } = useStagedActionInvoke()
  const { undoStagedAction, isUndoing, undoingId } = useStagedActionUndo()
  const connectionError = useConnectionError(action)
  const view = action.view
  // Only an editable draft has a title to flush before it runs (a failed one
  // still does — Retry runs what the user just fixed), and the row also serves
  // sent actions — leave their edit options unfetched.
  const editableActionId = isEditableActionStatus(status) ? action.id : undefined
  const { editOptions } = useStagedActionEditOptions({ actionId: editableActionId })
  const { commitTitle } = useActionInlineDraftController({
    actionId: editableActionId,
    coreTitle: editOptions?.title?.coreTitle,
    serverContent: view?.content,
  })
  // The CTA owns the title flush that precedes an invoke, so it has to look busy
  // for it too — otherwise a slow PATCH leaves an idle, clickable Execute and a
  // second press starts a second invoke.
  const [isFlushingEdits, setIsFlushingEdits] = useState(false)

  // An undrafted message would open an empty compose, so it only offers drafting
  const draftControl = useDraftAction(action)
  const isUndrafted = isUndraftedAction(action)
  // Slack's view renders an empty body as "No content", which isn't anything to copy
  const copyContent = isUndrafted ? undefined : view?.content
  const opensInApp =
    !isUndrafted && (status === 'staged' || status === 'error') && openInAppLink(action, () => undefined) !== null
  // The ⌘↩ chips cost ~46px, which at the minimum sidepanel width (a 384px row)
  // is the difference between "Add Section" rendering in full and truncating to
  // "Add Se…". Only the chips go. The keydown listener below still binds ⌘↵.
  const showHotkeyChips = showHotkeys && !isCompact && !opensInApp

  const isSent = state === 'sent'
  const isStaged = status === 'staged'
  const isInvoking = status === 'invoking'
  const actionHasError = status === 'error'
  const hasUndoError = status === 'undo-error'
  const resultButtons = view?.buttons ?? []
  const primaryLink = resultButtons[0]?.link
  const isInvokingThis = isInvoking || isInvokingThisWindow || isFlushingEdits
  const isReport = isReportToUserStagedAction(action)
  // Clearing a report archives it so it remains available in History.
  const archive = useActionArchiveControl(action)
  const isConversation = isConversationStagedAction(action)
  const addAsTodo = useAddActionAsTodo(action)
  const isUndoingThis = undoingId === action.id
  // A failed undo keeps the button so the user can retry; otherwise there has
  // to be a result to reverse.
  const canUndo = isSent && !!view?.supportsUndo && (!!action.outputData || hasUndoError)
  const size = FOOTER_SIZES[minimalSentControls ? 'minimal' : 'default']

  const handleInvoke = useCallback(() => {
    if (view?.invokeLink) {
      // A destination, not a server-side run — nothing to flush, and awaiting
      // here would spend the user gesture `window.open` needs.
      window.open(view.invokeLink, '_blank')
      trackActionEvent(action, 'invoke', { status, wasEdited: isStagedActionUserEdited(action) })
      return
    }
    // Flush a title still sitting in the field first, so the action runs with
    // what the user is looking at. Needed for ⌘↵, which this row hears on a
    // window capture listener — before the field's own blur commits it. A no-op
    // once that blur has fired (a click on Execute), and the second caller joins
    // the same in-flight commit rather than sending it twice.
    const run = async () => {
      setIsFlushingEdits(true)
      try {
        const titleCommitted = await commitTitle()
        if (!titleCommitted) {
          return
        }
        trackActionEvent(action, 'invoke', { status, wasEdited: isStagedActionUserEdited(action) })
        await invokeStagedAction(action.id)
      } finally {
        setIsFlushingEdits(false)
      }
    }
    run().catch(console.error)
  }, [action, status, view?.invokeLink, invokeStagedAction, commitTitle])

  // Behind the notch redesign, an unsent action can also be finished in its own app
  const latestAction = useRef(action)
  latestAction.current = action
  const openInApp = opensInApp ? openInAppLink(action, () => sendBody(latestAction.current) || undefined) : null
  const handleOpenInApp = useCallback(() => {
    if (!openInApp) return
    // Opened inside the click's gesture; the link doesn't carry the title, so its commit can trail
    openInApp.open()
    trackActionEvent(action, 'open-in-app', { status, wasEdited: isStagedActionUserEdited(action) })
    commitTitle().catch(console.error)
  }, [action, commitTitle, openInApp, status])

  const handleUndo = useCallback(() => {
    undoStagedAction(action.id).catch(console.error)
    trackActionEvent(action, 'undo', { status })
  }, [action, status, undoStagedAction])

  const handleOpenResult = useCallback(() => {
    if (primaryLink) {
      trackStagedActionOpenLink(action, { source: 'action-view', trigger: 'hotkey' })
      window.open(primaryLink, '_blank')
    }
  }, [primaryLink, action])

  // The utility circle: undo once the action has been sent, otherwise copy the
  // content. Not gated on the state — an integration that can't undo (Terminal,
  // Claude Code, a plan) would leave the slot empty after sending, and copying
  // what was sent is more use there than a gap.
  const utilitySlot: ControlSlot | null = canUndo
    ? {
        key: 'undo',
        node: minimalSentControls ? (
          <Button
            onClick={handleUndo}
            disabled={isUndoing}
            loading={isUndoingThis}
            variant="outline"
            tone={hasUndoError ? 'danger' : undefined}
            className={size.pill}
            left={<Undo2 aria-hidden="true" />}
          >
            {hasUndoError ? 'Retry undo' : 'Undo'}
          </Button>
        ) : (
          <Tooltip>
            <Tooltip.Trigger
              render={
                <Button
                  variant="outline"
                  tone={hasUndoError ? 'danger' : undefined}
                  shape="square"
                  aria-label={hasUndoError ? 'Retry undo' : 'Undo action'}
                  onClick={handleUndo}
                  disabled={isUndoing}
                  loading={isUndoingThis}
                  className={FLOATING_FILL_CLASS}
                >
                  <Undo2 aria-hidden="true" />
                </Button>
              }
            />
            <Tooltip.Content>
              {hasUndoError ? 'Retry undo' : 'Undo'}
              {showHotkeys && <Tooltip.Hotkey hotkey="cmd+z" />}
            </Tooltip.Content>
          </Tooltip>
        ),
      }
    : copyContent
      ? {
          key: 'copy',
          node: (
            <CopyIconButton
              value={copyContent}
              icon="content"
              ariaLabel="Copy content"
              className={size.circle}
              onCopy={() => trackStagedActionCopy(action, 'content', { source: 'action-view' })}
            />
          ),
        }
      : null

  // The CTA pill: reconnect → retry → execute before the action runs, the
  // output link after. `onHotkey` rides along, so ⌘↵ always does exactly what
  // the pill on screen does — including nothing while it's loading. Written as
  // a ladder of early returns because that's what it is: the first condition
  // that holds owns the position.
  const resolvePrimarySlot = (): ControlSlot | null => {
    if (isSent) {
      if (resultButtons.length === 0) return null
      return {
        key: 'result',
        onHotkey: handleOpenResult,
        node: (
          <div className="flex items-center gap-2">
            {resultButtons.map((button, index) => (
              <Button
                key={button.link}
                onClick={() => {
                  trackStagedActionOpenLink(action, { source: 'action-view' })
                  window.open(button.link, '_blank')
                }}
                // Secondary, not the brand-yellow CTA: the work is done, so
                // this is a destination rather than the thing asking to be
                // run. It also keeps the integration glyph and the hotkey
                // chips legible, which an inverted fill swallows.
                variant="outline"
                className={size.pill}
                left={getActionIcon(action, size.icon)}
                hotkey={showHotkeyChips && index === 0 ? 'cmd + enter' : undefined}
              >
                {button.label}
              </Button>
            ))}
          </div>
        ),
      }
    }

    if (connectionError) {
      return { key: 'reconnect', node: <ReconnectButton action={action} floating /> }
    }

    if (actionHasError) {
      return {
        key: 'retry',
        onHotkey: isInvokingThis ? undefined : handleInvoke,
        node: (
          <Button
            onClick={handleInvoke}
            variant="outline"
            tone="danger"
            className={FLOATING_DANGER_FILL_CLASS}
            disabled={isInvokingThis}
            loading={isInvokingThis}
            hotkey={showHotkeyChips ? 'cmd + enter' : undefined}
          >
            Retry
          </Button>
        ),
      }
    }

    // Nothing to run, but something to be done with: clearing a delivered
    // message is the move, so it holds the CTA position rather than leaving
    // Archive — a utility circle — as the only way to make the card go away. An
    // archived report has nothing left to dismiss; Restore is in the cluster.
    if (isReport) {
      if (archive.isArchived) return null
      return {
        key: 'report-dismiss',
        onHotkey: archive.onSelect,
        node: (
          <Button
            onClick={archive.onSelect}
            variant="primary"
            className={floatingCtaClass}
            left={<ThumbsUp aria-hidden="true" />}
            hotkey={showHotkeyChips ? 'cmd + enter' : undefined}
          >
            <span className="truncate">Clear</span>
          </Button>
        ),
      }
    }

    // A conversation action asks first: keep it as a todo, or start its chat now. Archive declines it.
    if (isConversation && (isStaged || isInvoking)) {
      return {
        key: 'add-todo',
        onHotkey: isInvokingThis ? undefined : addAsTodo,
        node: (
          <div className="flex min-w-0 items-center gap-2">
            <Button
              onClick={handleInvoke}
              variant="outline"
              className={twMerge(size.pill, floatingCtaClass)}
              disabled={isInvokingThis}
              loading={isInvokingThis}
              left={<MessageSquarePlus aria-hidden="true" />}
            >
              <span className="truncate">Start chat</span>
            </Button>
            <Button
              onClick={addAsTodo}
              variant="primary"
              className={floatingCtaClass}
              disabled={isInvokingThis}
              left={<ListChecks aria-hidden="true" />}
              hotkey={showHotkeyChips ? 'cmd + enter' : undefined}
            >
              <span className="truncate">Add to todos</span>
            </Button>
          </div>
        ),
      }
    }

    if (isStaged && (draftControl.canDraft || draftControl.isDrafting)) {
      return {
        key: 'draft',
        onHotkey: draftControl.canDraft ? draftControl.draft : undefined,
        node: (
          <Button
            onClick={draftControl.draft}
            variant="primary"
            className={floatingCtaClass}
            disabled={!draftControl.canDraft}
            loading={draftControl.isDrafting}
            left={<Sparkles aria-hidden="true" />}
            hotkey={showHotkeyChips ? 'cmd + enter' : undefined}
          >
            <span className="truncate">{draftControl.label}</span>
          </Button>
        ),
      }
    }

    if (isStaged || isInvoking) {
      return {
        key: 'invoke',
        onHotkey: isInvokingThis ? undefined : handleInvoke,
        node: (
          <Button
            onClick={handleInvoke}
            variant="primary"
            className={floatingCtaClass}
            disabled={isInvokingThis}
            loading={isInvokingThis}
            left={<InvokeIcon icon={view?.invokeIcon} />}
            hotkey={showHotkeyChips ? 'cmd + enter' : undefined}
          >
            <span className="truncate">{view?.invokeLabel ?? 'Execute'}</span>
          </Button>
        ),
      }
    }

    return null
  }

  const primarySlot = resolvePrimarySlot()

  // ⌘↵ runs the primary slot's action, whatever it currently is; ⌘Z undoes.
  // Capture phase: the editable body's HardBreak extension also binds
  // Mod-Enter and would insert a newline before a bubble-phase listener heard it.
  const onPrimaryHotkey = enableHotkeys ? primarySlot?.onHotkey : undefined
  const onUndoHotkey = enableHotkeys && canUndo && !isUndoing ? handleUndo : undefined
  useEffect(() => {
    if (!onPrimaryHotkey && !onUndoHotkey) {
      return
    }
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return
      // Capture fires before the focused element, so a textarea's own
      // stopPropagation can't protect it here — skip native text fields
      // (e.g. the feedback popover) or ⌘↵ invokes instead of submitting.
      if (isNativeTextEntryTarget(e.target)) return
      const run = e.key === 'Enter' ? onPrimaryHotkey : e.key === 'z' ? onUndoHotkey : undefined
      if (!run) return
      e.preventDefault()
      e.stopPropagation()
      run()
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [onPrimaryHotkey, onUndoHotkey])

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      {isStaged && view?.invokeWarning && (
        <div className="type-ui-sm flex min-w-0">
          <ActionWarning warning={view.invokeWarning} />
        </div>
      )}
      {draftControl.failed && (
        <ActionViewErrorBanner>Couldn&apos;t draft it. Try again, or write it yourself.</ActionViewErrorBanner>
      )}
      {(connectionError || actionHasError || hasUndoError) && errorMessage && (
        <ActionViewErrorBanner>{errorMessage}</ActionViewErrorBanner>
      )}
      {/* A failed scoped-chat edit lands back here — surface why. The fallback
          conversation (if one was created) stays reachable through the header's
          source attribution. */}
      {editState?.status === 'error' && (
        <ActionViewErrorBanner>{editState.message || 'Edit could not be applied'}</ActionViewErrorBanner>
      )}
      {/* One row in both chromes — the action controls stay on the right. Compact
          has no left cluster (the meeting owns that end of the bar) and folds the
          utility controls into the menu, so it holds the position with an empty
          span for `justify-between` to push against. */}
      <div className="flex min-w-0 items-center justify-between gap-2">
        {isCompact || minimalSentControls ? (
          <span />
        ) : (
          <ActionViewUtilityCluster
            action={action}
            braintrustLink={braintrustLink}
            chat={chat}
            onArchive={archive.isArchived ? undefined : archive.onSelect}
          />
        )}
        <div className="flex min-w-0 items-center gap-2">
          {!isCompact && (
            <ActionViewChatCircleNeighbours hidden={chat?.applyingChanges === true}>
              <ActionArchiveCircle
                label={archive.label}
                isArchived={archive.isArchived}
                onSelect={archive.onSelect}
                className={size.circle}
              />
            </ActionViewChatCircleNeighbours>
          )}
          {isCompact ? (
            <ActionViewOverflowMenu
              action={action}
              braintrustLink={braintrustLink}
              copyContent={copyContent}
              undo={
                canUndo
                  ? { label: hasUndoError ? 'Retry undo' : 'Undo', disabled: isUndoing, onSelect: handleUndo }
                  : undefined
              }
            />
          ) : (
            <ActionViewMorphSlot slot={utilitySlot} />
          )}
          {openInApp && (
            <Button
              onClick={handleOpenInApp}
              variant="outline"
              className={twMerge(size.pill, 'min-w-0 shrink')}
              left={getActionIcon(action, size.icon)}
            >
              <span className="truncate">{openInApp.label}</span>
            </Button>
          )}
          {/* Send keeps its label; the Open in <app> beside it gives up width first */}
          <ActionViewMorphSlot slot={primarySlot} className={openInApp ? 'shrink-0' : 'min-w-0 shrink'} />
        </div>
      </div>
    </div>
  )
}
